import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Check, Building2, Home, TreeDeciduous, MapPin, IndianRupee } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
    { id: 'start', title: 'Find Your Dream Property' },
    { id: 'type', title: 'What are you looking for?' },
    { id: 'budget', title: 'What is your budget?' },
    { id: 'location', title: 'Preferred City?' },
    { id: 'results', title: 'Recommended for You' }
];

export default function SurveyModal({ isOpen, onClose }) {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [formData, setFormData] = useState({
        intent: 'buy',
        property_type: '',
        min_price: null,
        max_price: null,
        location_city: '',
        bedrooms: null
    });
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(false);

    // Auto-open logic can be handled by parent or here if we want default behavior

    const handleNext = () => setStep((prev) => prev + 1);

    const submitSurvey = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/survey/submit`, formData);
            setRecommendations(response.data.recommendations || []);
            handleNext();
        } catch (error) {
            console.error('Survey submission failed:', error);
            // Fallback or error handling
            handleNext();
        } finally {
            setLoading(false);
        }
    };

    const handleOptionSelect = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        if (key === 'intent' || key === 'property_type') {
            handleNext();
        }
    };

    const handleBudgetSelect = (min, max) => {
        setFormData(prev => ({ ...prev, min_price: min, max_price: max }));
        handleNext();
    };

    const reset = () => {
        setStep(0);
        setFormData({
            intent: 'buy',
            property_type: '',
            min_price: null,
            max_price: null,
            location_city: '',
            bedrooms: null
        });
        setRecommendations([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/50 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden relative"
                >
                    <button
                        onClick={reset}
                        className="absolute top-4 right-4 text-navy-400 hover:text-navy-600 z-10"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    {/* Progress Bar */}
                    {step > 0 && step < steps.length - 1 && (
                        <div className="h-1 bg-navy-50 w-full absolute top-0">
                            <div
                                className="h-full bg-sky-500 transition-all duration-300"
                                style={{ width: `${(step / (steps.length - 2)) * 100}%` }}
                            />
                        </div>
                    )}

                    <div className="p-8">
                        {step === 0 && (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Home className="w-8 h-8 text-sky-600" />
                                </div>
                                <h2 className="text-2xl font-bold text-navy-900 mb-3">Find Your Perfect Home</h2>
                                <p className="text-navy-600 mb-8">
                                    Answer 3 simple questions and we'll find properties that match your lifestyle.
                                </p>
                                <Button onClick={handleNext} size="lg" className="w-full bg-sky-500 hover:bg-sky-600">
                                    Start Finding
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </Button>
                            </div>
                        )}

                        {step === 1 && (
                            <div>
                                <h3 className="text-xl font-semibold text-navy-900 mb-6">What type of property?</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    {[
                                        { id: 'apartment', label: 'Apartment', icon: Building2 },
                                        { id: 'villa', label: 'Villa', icon: Home },
                                        { id: 'plot', label: 'Plot', icon: MapPin },
                                        { id: 'land', label: 'Land', icon: TreeDeciduous }
                                    ].map((type) => (
                                        <button
                                            key={type.id}
                                            onClick={() => handleOptionSelect('property_type', type.id)}
                                            className="flex flex-col items-center justify-center p-6 rounded-xl border-2 border-navy-100 hover:border-sky-500 hover:bg-sky-50 transition-all group"
                                        >
                                            <type.icon className="w-8 h-8 text-navy-400 group-hover:text-sky-500 mb-3" />
                                            <span className="font-medium text-navy-900 group-hover:text-sky-700">{type.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div>
                                <h3 className="text-xl font-semibold text-navy-900 mb-6">What is your budget?</h3>
                                <div className="space-y-3">
                                    {[
                                        { label: 'Under ₹50 Lakhs', min: 0, max: 5000000 },
                                        { label: '₹50 L - ₹1 Cr', min: 5000000, max: 10000000 },
                                        { label: '₹1 Cr - ₹3 Cr', min: 10000000, max: 30000000 },
                                        { label: 'Above ₹3 Cr', min: 30000000, max: null }
                                    ].map((range, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => handleBudgetSelect(range.min, range.max)}
                                            className="w-full p-4 rounded-xl border border-navy-100 hover:border-sky-500 hover:bg-sky-50 text-left flex items-center justify-between group transition-all"
                                        >
                                            <span className="font-medium text-navy-700 group-hover:text-sky-700">{range.label}</span>
                                            <IndianRupee className="w-4 h-4 text-navy-300 group-hover:text-sky-500" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div>
                                <h3 className="text-xl font-semibold text-navy-900 mb-6">Which city?</h3>
                                <div className="space-y-4">
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-3 w-5 h-5 text-navy-400" />
                                        <Input
                                            placeholder="Enter city (e.g. Mumbai, Bangalore)"
                                            className="pl-10 h-12"
                                            value={formData.location_city}
                                            onChange={(e) => setFormData(prev => ({ ...prev, location_city: e.target.value }))}
                                            onKeyDown={(e) => e.key === 'Enter' && formData.location_city && submitSurvey()}
                                        />
                                    </div>
                                    <Button
                                        onClick={submitSurvey}
                                        disabled={!formData.location_city || loading}
                                        className="w-full h-12 bg-sky-500 hover:bg-sky-600"
                                    >
                                        {loading ? 'Finding Best Matches...`' : 'Show Results'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div className="max-h-[60vh] overflow-y-auto pr-1">
                                <h3 className="text-xl font-semibold text-navy-900 mb-2">We found {recommendations.length} matches!</h3>
                                <p className="text-navy-500 mb-6 text-sm">Based on your preferences</p>

                                {recommendations.length > 0 ? (
                                    <div className="space-y-4">
                                        {recommendations.map(prop => (
                                            <div key={prop.id} className="flex gap-4 p-3 rounded-xl border border-navy-100 hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                                                onClose();
                                                navigate(`/agency/${prop.agency_slug}/property/${prop.id}`);
                                            }}>
                                                <img
                                                    src={getImageUrl(prop.images?.[0])}
                                                    alt={prop.title}
                                                    className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold text-navy-900 truncate">{prop.title}</h4>
                                                    <p className="text-xs text-navy-500 mb-1">{prop.location_area}, {prop.location_city}</p>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <span className="font-bold text-sky-600">
                                                            {prop.price >= 10000000 ? `₹${(prop.price / 10000000).toFixed(2)} Cr` : `₹${(prop.price / 100000).toFixed(2)} L`}
                                                        </span>
                                                        <span className="text-xs bg-navy-100 px-2 py-1 rounded text-navy-700 capitalize">{prop.property_type}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <Button
                                            onClick={() => {
                                                onClose();
                                                navigate('/search');
                                            }}
                                            variant="outline"
                                            className="w-full mt-4"
                                        >
                                            View All Properties
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-navy-500 mb-4">No exact matches found, but don't worry!</p>
                                        <Button onClick={() => { onClose(); navigate('/search'); }}>
                                            Browse All Properties
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
